import { Link } from 'react-router-dom';
import { HomeIcon } from '@heroicons/react/24/outline';
import SEO from '../components/SEO';

export default function SmeCorporationAccount() {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="SME and Corporate Current Account"
        description="Comprehensive banking for small and medium enterprises and corporate clients in Somalia. Payroll, trade finance and cash management from MyBank."
        keywords={['SME account Somalia', 'corporate banking Mogadishu', 'business current account', 'MyBank corporate']}
      />
      {/* Hero Section */}
      <div className="relative min-h-[300px] sm:h-[390px] bg-gradient-to-r from-[#001F5F] to-[#017EFF] flex items-center justify-center">
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-transparent" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex flex-col items-center space-y-6 sm:space-y-12 py-8 sm:py-0">
            {/* Breadcrumb */}
            <nav className="flex items-center justify-center space-x-1 sm:space-x-2 text-xs sm:text-sm text-white/90 flex-wrap">
              <Link to="/" className="hover:text-[#017EFF] transition-colors flex items-center">
                <HomeIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                Home
              </Link>
              <span className="text-white/60 mx-1 sm:mx-2">/</span>
              <Link to="/#banking-solutions" className="hover:text-[#017EFF] transition-colors hidden sm:inline">
                Corporate Banking
              </Link>
              <span className="text-white/60 mx-1 sm:mx-2 hidden sm:inline">/</span>
              <Link to="/services" className="hover:text-[#017EFF] transition-colors">
                Current Accounts
              </Link>
              <span className="text-white/60 mx-1 sm:mx-2">/</span>
              <span className="text-[#017EFF]">SME/Corporation Account</span>
            </nav>

            {/* Hero Content */}
            <div className="flex flex-col items-center max-w-4xl mx-auto">
              <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-white leading-tight text-center mb-4 sm:mb-6 px-2">
                SME/Corporation <span className="text-[#017EFF]">Current Account</span>
              </h1>
              <p className="text-sm sm:text-base md:text-lg lg:text-xl text-white/90 text-center leading-relaxed px-4 max-w-4xl">
                Comprehensive banking solutions for small & medium enterprises and corporate clients
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="container mx-auto px-4 -mt-8 sm:-mt-16 mb-12 sm:mb-24">
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-8 lg:p-12">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-20 items-start">
            {/* Left Column */}
            <div className="space-y-10">
              <div className="space-y-6">
                <h2 className="text-4xl font-bold text-[#001F5F]">
                  Robust Banking for SMEs & Corporations
                </h2>
                <div className="space-y-4 text-gray-700">
                  <p>
                    Whether you’re managing payroll, handling trade transactions, or optimizing cash flow, our SME/Corporation account adapts to your needs.
                  </p>
                  <p>
                    Enjoy seamless operations, real-time reporting, and dedicated support—so you can focus on growing your business.
                  </p>
                </div>
              </div>

              {/* Benefits List */}
              <div className="bg-gray-50 rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <h3 className="text-2xl font-bold text-[#001F5F] mb-8">
                  Account Benefits
                </h3>
                <div className="grid grid-cols-1 gap-6">
                  {[
                    "50‑leaf checkbook",
                    "Multi‑user online access",
                    "Bulk payments & payroll processing",
                    "Automated monthly e‑statements",
                    "Trade finance support",
                    "Advanced cash management tools",
                    "Real‑time SMS & email alerts",
                    "Dedicated relationship manager"
                  ].map((benefit, idx) => (
                    <div key={idx} className="flex items-center text-gray-700">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-4 flex-shrink-0">
                        <svg className="w-5 h-5 text-[#017EFF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span>{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="pt-4">
                <Link
                  to="/open-account"
                  className="inline-flex items-center px-8 py-4 bg-[#017EFF] text-white font-semibold rounded-lg hover:bg-[#0165CC] transition-all transform hover:scale-105 hover:shadow-lg"
                >
                  Open Account
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-8">
              {/* Requirements */}
              <div className="bg-[#001F5F] rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <h3 className="text-2xl font-bold text-white mb-8">
                  Requirements
                </h3>
                <div className="space-y-6">
                  {[
                    "Certificate of incorporation",
                    "Memorandum & Articles of Association",
                    "Board resolution for account opening",
                    "Valid IDs of directors/shareholders",
                    "Proof of registered business address",
                    "Recent passport‑size photos of signatories",
                    "Tax registration certificate",
                    "Letter of request with signing mandate"
                  ].map((req, idx) => (
                    <div key={idx} className="flex items-start text-white/90">
                      <div className="w-8 h-8 rounded-full bg-[#017EFF]/20 flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                        <svg className="w-5 h-5 text-[#017EFF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span>{req}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Additional Services */}
              {/* <div className="bg-gray-50 rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <h3 className="text-2xl font-bold text-[#001F5F] mb-6">
                  Additional Services
                </h3>
                <div className="space-y-4">
                  <Link to="/internet-and-mobile-banking" className="flex items-center text-gray-700 hover:text-[#017EFF] transition-colors">
                    <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                    Internet Banking
                  </Link>
                  <Link to="/trade-financing" className="flex items-center text-gray-700 hover:text-[#017EFF] transition-colors">
                    <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                    Trade Finance
                  </Link>
                  <Link to="/other-services" className="flex items-center text-gray-700 hover:text-[#017EFF] transition-colors">
                    <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                    Payroll Management
                  </Link>
                  <Link to="/swift" className="flex items-center text-gray-700 hover:text-[#017EFF] transition-colors">
                    <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                    Swift Transfers
                  </Link>
                </div>
              </div> */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
